import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PRIORITY_META, PRIORITY_ORDER } from "@/lib/meta";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ExternalLink } from "lucide-react";

const blank = {
  title: "", description: "", assigned_to: "", priority: "medium", priority_order: "",
  due_date: "", location: "", department: "", customer: "", sap_id: "", bricert_id: "",
};

const inputCls = "w-full px-3 py-2 border border-zinc-300 rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-brand";

export const JobEditDialog = ({ job, open, onOpenChange, onSaved, onOpenFull }) => {
  const [form, setForm] = useState(blank);
  const [riggers, setRiggers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/riggers").then((r) => setRiggers(r.data)).catch(() => {});
    api.get("/my-locations").then((r) => setLocations(r.data)).catch(() => {});
    api.get("/departments").then((r) => setDepartments(r.data)).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (job) {
      setForm({
        title: job.title || "", description: job.description || "", assigned_to: job.assigned_to || "",
        priority: job.priority || "medium", priority_order: job.priority_order ?? "",
        due_date: job.due_date || "", location: job.location || "", department: job.department || "",
        customer: job.customer || "", sap_id: job.sap_id || "", bricert_id: job.bricert_id || "",
      });
    }
  }, [job]);

  const submit = async (e) => {
    e.preventDefault();
    if (!job) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        priority_order: form.priority_order === "" || form.priority_order === null ? null : Number(form.priority_order),
      };
      const res = await api.put(`/jobs/${job.id}`, payload);
      toast.success("Job updated");
      onSaved?.(res.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const locationMissing = form.location && !locations.some((l) => l.name === form.location);
  const departmentMissing = form.department && !departments.some((d) => d.name === form.department);
  const assigneeMissing = form.assigned_to && !riggers.some((r) => r.id === form.assigned_to);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg rounded-none max-h-[90vh] overflow-y-auto" data-testid="board-job-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-black tracking-tighter">Edit Job</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-sm font-bold text-zinc-900 block mb-1">Title</label>
            <input
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="board-edit-title"
              className={inputCls}
            />
          </div>
          <div>
            <label className="text-sm font-bold text-zinc-900 block mb-1">
              Description <span className="text-zinc-400 font-normal">(optional)</span>
            </label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="board-edit-description"
              rows={3}
              className={inputCls}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-bold text-zinc-900 block mb-1">Assign Rigger</label>
              <select
                value={form.assigned_to}
                onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}
                data-testid="board-edit-assign"
                className={`${inputCls} bg-white`}
              >
                <option value="">— Unassigned —</option>
                {assigneeMissing && <option value={form.assigned_to}>{job?.assigned_to_name || "Assigned"}</option>}
                {riggers.map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-bold text-zinc-900 block mb-1">Priority</label>
              <select
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
                data-testid="board-edit-priority"
                className={`${inputCls} bg-white`}
              >
                {PRIORITY_ORDER.map((p) => (
                  <option key={p} value={p}>{PRIORITY_META[p].label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-bold text-zinc-900 block mb-1">
                Priority Order <span className="text-zinc-400 font-normal">(lower = higher)</span>
              </label>
              <input
                type="number"
                min="1"
                step="1"
                value={form.priority_order}
                onChange={(e) => setForm({ ...form, priority_order: e.target.value })}
                data-testid="board-edit-order"
                placeholder="e.g. 1"
                className={inputCls}
              />
            </div>
            <div>
              <label className="text-sm font-bold text-zinc-900 block mb-1">Due date</label>
              <input
                type="date"
                required
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                data-testid="board-edit-due"
                className={inputCls}
              />
            </div>
            <div>
              <label className="text-sm font-bold text-zinc-900 block mb-1">Location</label>
              <select
                required
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                data-testid="board-edit-location"
                className={`${inputCls} bg-white`}
              >
                <option value="" disabled>Select location</option>
                {locationMissing && <option value={form.location}>{form.location}</option>}
                {locations.map((l) => (
                  <option key={l.id} value={l.name}>{l.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-bold text-zinc-900 block mb-1">Department</label>
              <select
                required
                value={form.department}
                onChange={(e) => setForm({ ...form, department: e.target.value })}
                data-testid="board-edit-department"
                className={`${inputCls} bg-white`}
              >
                <option value="" disabled>Select department</option>
                {departmentMissing && <option value={form.department}>{form.department}</option>}
                {departments.map((d) => (
                  <option key={d.id} value={d.name}>{d.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-bold text-zinc-900 block mb-1">Customer</label>
              <input
                required
                value={form.customer}
                onChange={(e) => setForm({ ...form, customer: e.target.value })}
                data-testid="board-edit-customer"
                className={inputCls}
              />
            </div>
            <div>
              <label className="text-sm font-bold text-zinc-900 block mb-1">SAP ID</label>
              <input
                required
                value={form.sap_id}
                onChange={(e) => setForm({ ...form, sap_id: e.target.value })}
                data-testid="board-edit-sapid"
                className={inputCls}
              />
            </div>
            <div className="col-span-2">
              <label className="text-sm font-bold text-zinc-900 block mb-1">
                Bricert ID <span className="text-zinc-400 font-normal">(optional)</span>
              </label>
              <input
                value={form.bricert_id}
                onChange={(e) => setForm({ ...form, bricert_id: e.target.value })}
                data-testid="board-edit-bricertid"
                className={inputCls}
              />
            </div>
          </div>
          <DialogFooter className="flex items-center justify-between gap-2 sm:justify-between">
            {onOpenFull && job && (
              <button
                type="button"
                onClick={() => onOpenFull(job.id)}
                data-testid="board-edit-open-full"
                className="inline-flex items-center gap-1 text-sm font-semibold text-brand hover:underline"
              >
                <ExternalLink size={14} /> Full job page
              </button>
            )}
            <button
              type="submit"
              disabled={saving}
              data-testid="board-edit-submit"
              className="bg-brand text-white rounded-full font-bold tracking-tight hover:bg-brand-dark transition-colors px-6 py-2 disabled:opacity-60"
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default JobEditDialog;
